import { useEffect, useState } from "react";
import { ExternalLink, LogOut, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, getToken, setToken, apiError } from "@/lib/api";
import AdminLogin from "./AdminLogin";
import ContentTab from "./ContentTab";
import ListsTab from "./ListsTab";
import MediaTab from "./MediaTab";
import ContactTab from "./ContactTab";
import InquiriesTab from "./InquiriesTab";
import AccountTab from "./AccountTab";

export default function AdminApp() {
  const [auth, setAuth] = useState("checking");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (!getToken()) return setAuth(false);
      try {
        await api.get("/admin/me");
        setAuth(true);
      } catch {
        setToken(null);
        setAuth(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (auth !== true) return;
    api
      .get("/content")
      .then(({ data }) => setDraft(data))
      .catch((e) => toast.error(apiError(e)));
  }, [auth]);

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await api.put("/admin/content", draft);
      setDraft(data);
      toast.success("تم حفظ التغييرات ونشرها على الموقع");
    } catch (e) {
      if (e?.response?.status === 401) {
        setToken(null);
        setAuth(false);
        toast.error("انتهت الجلسة، سجّل الدخول من جديد");
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const update = (section, key, value) =>
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));

  const updateFr = (section, key, value) =>
    setDraft((d) => ({
      ...d,
      fr: {
        ...(d.fr || {}),
        [section]: { ...((d.fr || {})[section] || {}), [key]: value },
      },
    }));

  if (auth === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-forest-950">
        <Loader2 className="h-8 w-8 animate-spin text-gold" data-testid="admin-loading-spinner" />
      </div>
    );
  }

  if (!auth) return <AdminLogin onLogin={() => setAuth(true)} />;

  return (
    <div className="min-h-screen bg-cream-deep" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-gold/20 bg-forest-950">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-3">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="شعار حلاق" className="h-10 w-auto rounded-lg" />
            <div>
              <p className="font-head text-sm font-extrabold text-cream">لوحة تحكم حلاق</p>
              <p className="text-[11px] text-cream/50">إدارة محتوى الموقع</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-site-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-4 py-2 text-xs font-semibold text-cream transition hover:border-gold hover:text-gold"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              عرض الموقع
            </a>
            <button
              onClick={save}
              disabled={saving || !draft}
              data-testid="admin-save-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-gold px-5 py-2 text-xs font-bold text-forest-950 transition hover:bg-gold-light disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              حفظ ونشر
            </button>
            <button
              onClick={() => {
                setToken(null);
                setAuth(false);
              }}
              data-testid="admin-logout-button"
              aria-label="تسجيل الخروج"
              className="rounded-full border border-cream/20 p-2 text-cream/70 transition hover:border-brick hover:text-brick"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <Tabs defaultValue="content" dir="rtl">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-2xl bg-white p-1.5 shadow-sm" data-testid="admin-tabs-list">
            {[
              ["content", "المحتوى"],
              ["lists", "المميزات والأسئلة"],
              ["media", "شاشات التطبيق"],
              ["contact", "واتساب والتواصل"],
              ["fr", "النسخة الفرنسية"],
              ["inquiries", "الاستفسارات"],
              ["accounts", "الحسابات"],
            ].map(([v, label]) => (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={`admin-tab-${v}`}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-moss transition data-[state=active]:bg-forest-900 data-[state=active]:text-cream"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="content" className="mt-6">
            <ContentTab data={draft} onChange={update} />
          </TabsContent>
          <TabsContent value="lists" className="mt-6">
            <ListsTab data={draft} update={update} />
          </TabsContent>
          <TabsContent value="media" className="mt-6">
            <MediaTab />
          </TabsContent>
          <TabsContent value="contact" className="mt-6">
            <ContactTab draft={draft} update={update} />
          </TabsContent>
          <TabsContent value="fr" className="mt-6 space-y-6">
            <p className="rounded-xl bg-gold/10 px-4 py-3 text-xs leading-relaxed text-gold-dark" data-testid="admin-fr-hint">
              هذه النسخة الفرنسية من محتوى الموقع. اترك أي قسم فارغاً ليعرض الموقع تلقائياً النسخة العربية منه لزوار اللغة الفرنسية.
            </p>
            <ContentTab data={draft?.fr || {}} onChange={updateFr} idSuffix="-fr" />
            <ListsTab data={draft?.fr || {}} update={updateFr} idSuffix="-fr" />
          </TabsContent>
          <TabsContent value="inquiries" className="mt-6">
            <InquiriesTab />
          </TabsContent>
          <TabsContent value="accounts" className="mt-6">
            <AccountTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
